import type { ReactElement } from "react"
import {
  getFormattedPeriodInfo,
  getSelectedPeriodInfo,
  PeriodInfo,
} from "@core/period-info"
import { getCurrencyCode } from "@core/pricing"
import { PriceFormat } from "@core/types"
import {
  type ProductBox,
  ProductBoxButtonType,
} from "@gatsby-plugin-generic-page/types/productBoxTypes"
import useMenuQuery from "@gatsby-plugin-onestore/hooks/useMenuQuery"
import { useLocation } from "@reach/router"
import { get } from "lodash"
import { Helmet } from "react-helmet"
import { helmetJsonLdProp } from "react-schemaorg"
import type { AggregateOffer, Offer, Product, WithContext } from "schema-dts"
import useSiteMetadataQuery from "~/hooks/useSiteMetadataQuery"
import isEmpty from "~/lib/isEmpty"

type ProductContext = WithContext<Product>

function createSingleProductContext(
  name: string,
  url: string,
  price: string,
  logoUrl: string,
  brandName: string
): ProductContext {
  const offers: Offer = {
    "@type": "Offer",
    url,
    priceCurrency: getCurrencyCode(),
    price,
    availability: "https://schema.org/InStock",
    itemCondition: "https://schema.org/NewCondition",
    seller: {
      "@type": "Organization",
      name: brandName,
    },
  }

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    url,
    ...(logoUrl ? { image: logoUrl } : {}),
    brand: {
      "@type": "Brand",
      name: brandName,
    },
    offers,
  }
}

function createAggregateProductContext(
  name: string,
  url: string,
  lowPrice: string,
  offerCount: number,
  logoUrl: string,
  brandName: string
): ProductContext {
  const offers: AggregateOffer = {
    "@type": "AggregateOffer",
    url,
    priceCurrency: getCurrencyCode(),
    lowPrice,
    offerCount,
    availability: "https://schema.org/InStock",
  }

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    url,
    ...(logoUrl ? { image: logoUrl } : {}),
    brand: {
      "@type": "Brand",
      name: brandName,
    },
    offers,
  }
}

type Props = {
  boxes: ProductBox[]
}

export default function ProductBoxSchema({
  boxes,
}: Props): ReactElement | null {
  const site = useSiteMetadataQuery()
  const { menu } = useMenuQuery()
  const { pathname } = useLocation()
  const pageUrl = `${site.siteUrl}${pathname}`
  const logoUrl: string = get(menu, "flatData.logo[0].url", "")

  const schemaItems = boxes
    .filter(
      (box) =>
        !isEmpty(box.flatData.buttons) &&
        box.flatData.buttons[0].type === ProductBoxButtonType.PLAN &&
        !isEmpty(box.flatData.buttons[0].saleConfiguration) &&
        !isEmpty(
          box.flatData.buttons[0].saleConfiguration[0]?.flatData
            ?.defaultCloudBluePlan
        )
    )
    .map((box) => {
      const name = box.flatData.name.replace(/\[\/[^\]]+\]/g, "").trim()
      const { saleConfiguration, bundle } = box.flatData.buttons[0] || {}
      const periods = get(
        saleConfiguration[0],
        "flatData.defaultCloudBluePlan[0].flatData.periods"
      )

      const isDefaultBundleChecked =
        !isEmpty(bundle) && bundle[0]?.flatData.checkbox === true

      let price: string

      if (isDefaultBundleChecked) {
        const saleConfigBundlePlan = get(
          saleConfiguration[0],
          "flatData.bundlePlan"
        )
        const bundlePeriods = !isEmpty(saleConfigBundlePlan)
          ? get(saleConfigBundlePlan, "[0].flatData.periods")
          : get(bundle[0], "flatData.plan[0].flatData.periods")

        const mainPeriodInfo = getSelectedPeriodInfo(periods, undefined)
        const bundlePeriodInfo = !isEmpty(bundlePeriods)
          ? getSelectedPeriodInfo(bundlePeriods, undefined)
          : undefined

        if (mainPeriodInfo && bundlePeriodInfo) {
          price = new PeriodInfo(
            mainPeriodInfo.periodValue,
            bundlePeriodInfo.periodValue
          ).getFormatted(PriceFormat.REGISTER_NETTO_DOT_SEPARATED)
        } else {
          price = getFormattedPeriodInfo(
            periods,
            undefined,
            PriceFormat.REGISTER_NETTO_DOT_SEPARATED
          )
        }
      } else {
        price = getFormattedPeriodInfo(
          periods,
          undefined,
          PriceFormat.REGISTER_NETTO_DOT_SEPARATED
        )
      }

      return { name, price }
    })
    .filter(({ name, price }) => name !== "" && price !== "")

  if (isEmpty(schemaItems)) {
    return null
  }

  if (schemaItems.length === 1) {
    return (
      <Helmet
        script={[
          helmetJsonLdProp(
            createSingleProductContext(
              schemaItems[0].name,
              pageUrl,
              schemaItems[0].price,
              logoUrl,
              site.ogSiteName
            )
          ),
        ]}
      />
    )
  }

  const lowPrice = schemaItems.reduce((min, item) =>
    parseFloat(item.price) < parseFloat(min.price) ? item : min
  ).price

  return (
    <Helmet
      script={[
        helmetJsonLdProp(
          createAggregateProductContext(
            schemaItems[0].name,
            pageUrl,
            lowPrice,
            schemaItems.length,
            logoUrl,
            site.ogSiteName
          )
        ),
      ]}
    />
  )
}
